import React, { useState, useEffect, useRef } from 'react';
import { FaQrcode, FaTimes, FaStar, FaSmile, FaClipboardCheck, FaArrowRight, FaExpand, FaUsers, FaClipboardList } from 'react-icons/fa';

const QRCodeSurvey = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [isAnimating, setIsAnimating] = useState(false);
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [showThankYou, setShowThankYou] = useState(false);
    const expandedRef = useRef(null);
    const compactRef = useRef(null);

    // Animasi melayang
    useEffect(() => {
        const animateInterval = setInterval(() => {
            setIsAnimating(true);
            setTimeout(() => setIsAnimating(false), 2000);
        }, 4000);

        return () => clearInterval(animateInterval);
    }, []);

    // Tampilkan setelah 3 detik
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    // Scroll ke form saat expanded dan handle click outside
    useEffect(() => {
        if (isExpanded) {
            // Scroll ke form dengan offset untuk navbar
            setTimeout(() => {
                if (expandedRef.current) {
                    const rect = expandedRef.current.getBoundingClientRect();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    const targetY = rect.top + scrollTop - 100; // Offset 100px dari atas

                    window.scrollTo({
                        top: targetY,
                        behavior: 'smooth'
                    });
                }
            }, 100);

            // Disable scroll pada body
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isExpanded]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isExpanded &&
                expandedRef.current &&
                !expandedRef.current.contains(event.target) &&
                compactRef.current &&
                !compactRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isExpanded]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape' && isExpanded) {
                setIsExpanded(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isExpanded]);

    const handleRatingClick = (value) => {
        setRating(value);
        setTimeout(() => {
            setShowThankYou(true);
            setTimeout(() => {
                setShowThankYou(false);
                setIsExpanded(false);
                document.body.style.overflow = 'auto';
            }, 3000);
        }, 500);
    };

    const handleExpand = () => {
        setIsExpanded(true);
    };

    const handleClose = () => {
        setIsExpanded(false);
        document.body.style.overflow = 'auto';
    };

    if (!isVisible) return null;

    return (
        <>
            {/* QR Code Melayang - Posisi Kiri */}
            <div
                ref={compactRef}
                className={`fixed left-4 md:left-8 bottom-4 md:bottom-8 z-[100] transition-all duration-1000 ${isAnimating ? 'translate-y-[-15px]' : 'translate-y-0'
                    } ${isExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
                {/* Compact Mode */}
                <div
                    className="bg-white rounded-2xl shadow-2xl p-2 md:p-2 cursor-pointer hover:shadow-3xl transition-all duration-300 group"
                    onClick={handleExpand}
                >
                    {/* Small Icon - Survey */}
                    <div className="w-10 h-10 md:w-10 md:h-10 bg-gradient-to-br from-[#1E3A8A] to-[#0D9488] rounded-xl flex items-center justify-center mb-2 md:mb-3 relative overflow-hidden group-hover:scale-105 transition-transform">
                        <div className="absolute inset-0 bg-white/10"></div>
                        <FaUsers className="text-2xl md:text-3xl text-white relative z-10" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-white/20 rounded-full p-1 md:p-2">
                                <FaUsers className="text-sm md:text-base text-white" />
                            </div>
                        </div>
                    </div>

                    <div className="text-center">
                        <p className="text-[10px] md:text-xs text-gray-800">Survey</p>
                    </div>

                    {/* Info Tooltip */}
                    <div className="absolute -right-40 md:-right-48 top-1/2 -translate-y-1/2 bg-[#0F172A] text-white px-3 md:px-4 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-xs md:text-sm shadow-lg">
                        Klik untuk mengisi survey
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 rotate-45 w-2 h-2 bg-[#0F172A]"></div>
                    </div>
                </div>
            </div>

            {/* Overlay Background */}
            {isExpanded && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] transition-opacity duration-300"
                    onClick={handleClose}
                />
            )}

            {/* Expanded Mode - Fixed Center dengan z-index tinggi */}
            {isExpanded && (
                <div
                    ref={expandedRef}
                    className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[300] w-[90%] max-w-md animate-fadeIn"
                >
                    <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-6 max-h-[85vh] overflow-y-auto">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-3 border-b z-10">
                            <div className="flex items-center">
                                <FaClipboardCheck className="text-[#0D9488] text-xl mr-2" />
                                <h3 className="font-bold text-[#1E3A8A] text-lg">Survey Kepuasan</h3>
                            </div>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full"
                                aria-label="Tutup survey"
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>

                        {!showThankYou ? (
                            <>
                                {/* QR Code */}
                                <div className="bg-gradient-to-br from-[#1E3A8A] to-[#0D9488] rounded-xl p-4 mb-4 text-center">
                                    <FaQrcode className="text-6xl md:text-7xl text-white mx-auto mb-2" />
                                    <p className="text-white text-sm font-semibold">Scan QR Code</p>
                                    <p className="text-white/70 text-xs">atau isi langsung di bawah</p>
                                </div>

                                {/* Rating Stars */}
                                <div className="mb-4">
                                    <p className="text-sm font-medium text-gray-700 mb-2 text-center">
                                        Bagaimana pelayanan kami?
                                    </p>
                                    {/*<div className="flex justify-center space-x-1 md:space-x-2">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                //onClick={() => handleRatingClick(star)}
                                                onMouseEnter={() => setHoverRating(star)}
                                                onMouseLeave={() => setHoverRating(0)}
                                                className="text-2xl md:text-3xl transition-all duration-200 hover:scale-125 focus:outline-none"
                                                aria-label={`Rating ${star} bintang`}
                                            >
                                                <FaStar
                                                    className={`${(hoverRating || rating) >= star
                                                            ? 'text-yellow-400 drop-shadow-lg'
                                                            : 'text-gray-300'
                                                        } transition-colors`}
                                                />
                                            </button>
                                        ))}
                                    </div>*/}
                                    {rating > 0 && (
                                        <p className="text-center text-sm text-[#0D9488] mt-2 font-medium animate-fadeIn">
                                            {rating === 5 ? 'Sangat Puas! 🌟' :
                                                rating === 4 ? 'Puas! 😊' :
                                                    rating === 3 ? 'Cukup! 👍' :
                                                        rating === 2 ? 'Kurang Puas 😔' :
                                                            'Tidak Puas 😢'}
                                        </p>
                                    )}
                                </div>

                                {/* Quick Survey Options */}
                                <div className="space-y-2 mb-4">
                                    <p className="text-sm font-medium text-gray-700 text-center mb-3">
                                        Silahkan isi survey dibawah ini :
                                    </p>
                                    {[
                                        { label: 'Kecepatan Pelayanan', icon: '⚡' },
                                        { label: 'Keramahan Petugas', icon: '😊' },
                                        { label: 'Kemudahan Proses', icon: '✅' },
                                        { label: 'Fasilitas', icon: '🏢' }
                                    ].map((item, index) => (
                                        <button
                                            key={index}
                                            //onClick={() => handleRatingClick(4)}
                                            className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-[#0D9488]/5 hover:border-[#0D9488] border border-transparent transition-all text-sm group"
                                        >
                                            <span>{item.icon} {item.label}</span>
                                            <FaArrowRight className="text-gray-400 group-hover:text-[#0D9488] group-hover:translate-x-1 transition-all" />
                                        </button>
                                    ))}
                                </div>

                                {/* Link to Full Survey */}
                                <a
                                    href="/survey"
                                    className="block w-full text-center bg-gradient-to-r from-[#1E3A8A] to-[#0D9488] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 text-sm hover:scale-[1.02]"
                                >
                                    Isi Survey Lengkap
                                </a>

                                {/* Keyboard Hint */}
                                <p className="text-center text-xs text-gray-400 mt-3">
                                    Tekan <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">Esc</kbd> untuk menutup
                                </p>
                            </>
                        ) : (
                            /* Thank You Message */
                            <div className="text-center py-6">
                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                                    <FaSmile className="text-5xl text-green-500" />
                                </div>
                                <h4 className="text-xl font-bold text-[#1E3A8A] mb-2">
                                    Terima Kasih!
                                </h4>
                                <p className="text-gray-600 text-sm mb-4">
                                    Penilaian Anda sangat berharga untuk meningkatkan kualitas pelayanan kami.
                                </p>
                                <div className="flex justify-center space-x-1 mb-4">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <FaStar key={star} className="text-yellow-400 text-xl drop-shadow-sm" />
                                    ))}
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="text-[#0D9488] hover:text-[#0F766E] text-sm font-semibold hover:underline"
                                >
                                    Tutup
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Animations */}
            <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translate(-50%, -48%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .shadow-3xl {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        /* Custom scrollbar untuk form */
        .max-h-\\[85vh\\]::-webkit-scrollbar {
          width: 6px;
        }
        
        .max-h-\\[85vh\\]::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .max-h-\\[85vh\\]::-webkit-scrollbar-thumb {
          background: #0D9488;
          border-radius: 10px;
        }
      `}</style>
        </>
    );
};

export default QRCodeSurvey;